import { Box, Button, IconButton, TextField, Typography } from "@material-ui/core";
import { useFormik, Form, FormikProvider } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { Close } from "@material-ui/icons";
import { LoadingButton } from "@material-ui/lab";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { addCaja, initialMoney } from "src/redux/slices/caja";
import { chargeCashDrawer } from "src/redux/slices/cashdrawer";
import { Grid } from "@mui/material";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import Calculator from "../calculator";
import React, { useState } from "react";
import useResponsive from "src/hooks/useResponsive";
import OpeningCountDialog from "src/views/app/UserNormal/caja/OpeningCountDialog";
import CashDrawerWaitDialog from "src/views/app/UserNormal/cartaCamarero/payment/Inline/CashDrawerWaitDialog";

export default function PopUpInputReferenceWithoutDialog({
  message,
  inputs,
  closeFunc,
  arqueo,
  cajaInicial,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const myRef = React.useRef({});
  const width = useResponsive("down", "sm");
  const [countDialogOpen, setCountDialogOpen] = useState(false);
  const currentActiveEmployee = useSelector(
    (state) => state.employees?.currentActiveEmployee,
  );

  // Integración cajón automático: mismo patrón que PopUpInputReference.
  // Los movimientos manuales (ingreso/retiro) pasan primero por el
  // cajón físico; el arqueo solo se registra si el cajón completa OK.
  const cashDrawerActive = useSelector(
    (s) => s.cashdrawer?.config?.status === "active"
      && s.cashdrawer?.config?.alternativePaymentId != null,
  );
  const [cashDrawerTx, setCashDrawerTx] = React.useState(null);

  const CashCountSchema = Yup.object().shape({
    cashCount: Yup.number(),
    substractCount: Yup.number(),
    initialCount: Yup.number(),
    cashCountNote: Yup.string(),
    cashSubstractNote: Yup.string(),
  });

  const ChangeValueCalculatorCallback = (importe) => {
    if (importe !== 0 && importe !== null)
      if (inputs.find((u) => u.target === "cashCount") !== undefined)
        setFieldValue("cashCount", importe);
      else if (inputs.find((u) => u.target === "initialCount") !== undefined)
        setFieldValue("initialCount", importe);
      else if (inputs.find((u) => u.target === "substractCount") !== undefined)
        setFieldValue("substractCount", importe);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      cashCount: 0,
      substractCount: 0,
      initialCount: arqueo?.initialMoney || 0,
      cashCountNote: "",
      cashSubstractNote: "",
    },
    validationSchema: CashCountSchema,
    onSubmit: async (values, { setSubmitting, resetForm, setErrors }) => {
      try {
        let data = {};
        let goNext = false;

        if (
          inputs.find((u) => u.target === "cashCount") !== undefined &&
          values.cashCount > 0
        ) {
          data = {
            idArqueo: arqueo.id,
            initialMoney: Number(values.cashCount),
            motivo: values.cashCountNote,
          };
          goNext = true;
        } else if (
          inputs.find((u) => u.target === "initialCount") !== undefined &&
          values.initialCount >= 0
        ) {
          // Permitir 0€ para caja inicial — abrir con caja vacía es un
          // caso válido. Los ingresos/retiros siguen exigiendo >0.
          data = {
            idArqueo: arqueo.id,
            initialMoney: Number(values.initialCount),
          };
          goNext = true;
        } else if (
          inputs.find((u) => u.target === "substractCount") !== undefined &&
          values.substractCount > 0
        ) {
          data = {
            idArqueo: arqueo.id,
            initialMoney: -Number(values.substractCount),
            motivo: values.cashSubstractNote,
          };
          goNext = true;
        }

        if (goNext) {
          const isRetiro = data.initialMoney < 0;
          const importeAbs = Math.abs(data.initialMoney);
          // Cajón físico primero — ver comentario homólogo en
          // `PopUpInputReference.js` (mismo criterio: no en caja inicial,
          // no en movimientos de 0€).
          if (cashDrawerActive && !cajaInicial && importeAbs > 0) {
            try {
              const res = await dispatch(chargeCashDrawer({
                amountCents: Math.round(importeAbs * 100),
                type: isRetiro ? "payout" : "sale",
              }));
              setCashDrawerTx({
                transactionId: res.transactionId,
                amountCents: Math.round(importeAbs * 100),
                pendingData: data,
                resetForm,
              });
            } catch (err) {
              enqueueSnackbar(
                err?.response?.data?.message
                  || "No se pudo enviar la operación al cajón automático",
                { variant: "error" },
              );
            }
            return;
          }
          if (cajaInicial) {
            dispatch(initialMoney(data))
              .then((res) => {
                enqueueSnackbar(t("caja.update"), {
                  variant: "success",
                });
                resetForm();
                closeFunc();
              })
              .catch((err) => {
                enqueueSnackbar(err, {
                  variant: "error",
                });
              });
          } else {
            dispatch(addCaja(data))
              .then((res) => {
                enqueueSnackbar(t("caja.update"), {
                  variant: "success",
                });
                resetForm();
                closeFunc();
              })
              .catch((err) => {
                enqueueSnackbar(err, {
                  variant: "error",
                });
              });
          }
        } else {
          enqueueSnackbar("Datos insuficientes", {
            variant: "error",
          });
        }
      } catch (error) {
        console.error(error);
        setSubmitting(false);
        setErrors(error);
      }
    },
  });

  const {
    handleSubmit,
    isSubmitting,
    setFieldValue,
    getFieldProps,
    touched,
    errors,
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                p: 4,
                maxWidth: 480,
                margin: "auto",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" sx={{ mb: 2 }}>
                {message}
              </Typography>
              {inputs.map((inp, index) => (
                <TextField
                  key={index}
                  fullWidth
                  label={inp.label}
                  {...getFieldProps(inp.target)}
                  error={Boolean(touched[inp.target] && errors[inp.target])}
                  helperText={touched[inp.target] && errors[inp.target]}
                  sx={{ mt: 1, mb: 1 }}
                />
              ))}

              <LoadingButton
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                pending={isSubmitting}
              >
                <Typography variant="h4">Guardar</Typography>
              </LoadingButton>

              {/* Botón secundario para el conteo detallado por
                  denominaciones — abre `OpeningCountDialog` (mismo
                  layout que `ArqueoCount` de cierre). Solo cuando el
                  modal se usa para APERTURA (cajaInicial=true). */}
              {cajaInicial && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  startIcon={<PointOfSaleIcon />}
                  onClick={() => setCountDialogOpen(true)}
                  sx={{ mt: 1 }}
                >
                  Contar por monedas
                </Button>
              )}

              <IconButton
                aria-label="close"
                onClick={closeFunc}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <Close />
              </IconButton>
            </Box>
          </Grid>
          {!width && (
            <Grid item xs={12} sm={6}>
              <Grid container>
                <Grid item xs={12} padding={4}>
                  <Calculator
                    callback={ChangeValueCalculatorCallback}
                    ref={myRef}
                    justNumber={true}
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>

        {/* Diálogo de conteo por denominaciones — mismo layout que el
            ArqueoCount de cierre (denominaciones + calculadora sticky +
            total prominente). Al confirmar dispara `initialMoney` con
            el desglose y cierra ambos modales. */}
        {cashDrawerTx && (
          <CashDrawerWaitDialog
            transactionId={cashDrawerTx.transactionId}
            amountCents={cashDrawerTx.amountCents}
            onResolved={async (tx) => {
              const pending = cashDrawerTx;
              setCashDrawerTx(null);
              if (!tx) return;
              if (tx.status !== "completed") {
                const msg = tx.errorMessage
                  || (tx.status === "cancelled" ? "Operación cancelada en el cajón"
                    : tx.status === "timeout" ? "El cajón no respondió a tiempo"
                    : tx.status === "partial" ? "El cajón sólo procesó parte del importe — repite el movimiento"
                    : "El cajón no completó la operación");
                enqueueSnackbar(msg, { variant: "error" });
                return;
              }
              try {
                await dispatch(addCaja(pending.pendingData));
                enqueueSnackbar(t("caja.update"), { variant: "success" });
                pending.resetForm?.();
                closeFunc();
              } catch (err) {
                enqueueSnackbar(err?.message || "Error al registrar el movimiento", {
                  variant: "error",
                });
              }
            }}
            onClose={() => setCashDrawerTx(null)}
          />
        )}

        {cajaInicial && (
          <OpeningCountDialog
            open={countDialogOpen}
            onClose={() => setCountDialogOpen(false)}
            initialBilletes={arqueo?.openingBilletes || null}
            onConfirm={async ({ billetes, total }) => {
              try {
                await dispatch(
                  initialMoney({
                    idArqueo: arqueo?.id,
                    initialMoney: Number(total),
                    billetes,
                    idEmployee: currentActiveEmployee || 0,
                  }),
                );
                enqueueSnackbar("Dinero inicial guardado con conteo detallado", {
                  variant: "success",
                });
                setCountDialogOpen(false);
                closeFunc();
              } catch (err) {
                enqueueSnackbar("Error al abrir la caja", {
                  variant: "error",
                });
              }
            }}
          />
        )}
      </Form>
    </FormikProvider>
  );
}
